import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLang, IMAGES } from "../i18n";
import { useLenis } from "../hooks/useLenis";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { LeadForm } from "../components/LeadForm";
import { Seo } from "../components/Seo";
import { Reveal, SectionLabel, MaskLine } from "../components/Motion";
import { ArrowLeft, ArrowUpRight, Sun, Leaf, Wind, ScanSearch, ShoppingBag, Mountain } from "lucide-react";

const HARVEST_ICONS = [Sun, Leaf, Wind, ScanSearch];

export default function Origano() {
  useLenis();
  const { t } = useLang();
  const o = t.origano;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="grain relative">
      <Seo page="origano" />
      <Nav />
      <main>
        {/* HERO */}
        <section data-testid="origano-hero" className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden">
          <div className="absolute -top-24 right-0 w-[560px] h-[560px] rounded-full bg-[#83B94A]/15 blur-[160px] pointer-events-none" />
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-14 items-center">
            <div className="lg:col-span-7">
              <Link to="/" data-testid="origano-back" className="inline-flex items-center gap-2 font-jetbrains text-[11px] tracking-[0.2em] uppercase text-[var(--text-secondary)] hover:text-[var(--gold)] transition-colors mb-10">
                <ArrowLeft className="w-4 h-4" /> Amazonico3000
              </Link>
              <SectionLabel>{o.label}</SectionLabel>
              <h1 className="font-cormorant font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
                <MaskLine delay={0.1}>{o.title}</MaskLine>
              </h1>
              <Reveal delay={0.25}>
                <p className="font-cormorant italic text-2xl md:text-3xl text-[var(--text-secondary)] mt-6 max-w-xl">{o.subtitle}</p>
                <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl">{o.intro}</p>
                <Link to="/tenute" data-testid="origano-tenute-cta" className="inline-flex items-center gap-3 mt-10 rounded-full border border-[var(--gold)] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors duration-300">
                  {o.tenuteCta} <ArrowUpRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
            <Reveal delay={0.25} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-[2rem] border border-[var(--border)] group">
                <img src={IMAGES.oreganoScan} alt="Origano delle Tenute Amazonico osservato da Oregano Scan" data-testid="origano-hero-img" className="w-full h-full object-cover transition-transform duration-[1.6s] ease-out group-hover:scale-105" />
                <span className="absolute top-6 left-6 w-6 h-6 border-t border-l border-[#83B94A]/70" />
                <span className="absolute bottom-6 right-6 w-6 h-6 border-b border-r border-[#83B94A]/70" />
              </div>
            </Reveal>
          </div>
        </section>

        {/* STORY */}
        <section data-testid="origano-story" className="py-16 md:py-24">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.storyLabel}</SectionLabel>
            <div className="grid lg:grid-cols-12 gap-10 mt-6">
              <h2 className="lg:col-span-6 font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{o.storyTitle}</h2>
              <div className="lg:col-span-6 space-y-5 font-outfit font-light text-[var(--text-secondary)] leading-relaxed">
                <p>{o.storyText}</p>
                <p className="font-cormorant italic text-xl md:text-2xl text-[var(--text-primary)]">{o.storyText2}</p>
              </div>
            </div>
          </div>
        </section>

        {/* CULTIVATION */}
        <section data-testid="origano-grow" className="py-16 md:py-24 bg-[var(--bg-secondary)] border-y border-[var(--border)]">
          <div className="max-w-[1200px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.growLabel}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)] mt-6 max-w-2xl">{o.growTitle}</h2>
            <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-3xl">{o.growText}</p>
            <ul className="grid sm:grid-cols-2 gap-x-10 mt-10 max-w-3xl">
              {o.growPoints.map((pt, i) => (
                <li key={i} data-testid={`origano-grow-point-${i + 1}`} className="flex items-center gap-3 border-b border-[var(--border)] py-3.5">
                  <Mountain className="w-4 h-4 text-[#83B94A] shrink-0" />
                  <span className="font-outfit text-[var(--text-primary)]">{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* HARVEST */}
        <section data-testid="origano-harvest" className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <SectionLabel>{o.harvestLabel}</SectionLabel>
            <div className="grid lg:grid-cols-12 gap-10 mt-6 mb-14">
              <h2 className="lg:col-span-5 font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--text-primary)]">{o.harvestTitle}</h2>
              <p className="lg:col-span-7 font-outfit font-light text-[var(--text-secondary)] leading-relaxed">{o.harvestText}</p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {o.harvestSteps.map((s, i) => {
                const Icon = HARVEST_ICONS[i] || Leaf;
                return (
                  <Reveal key={i} delay={i * 0.08}>
                    <div data-testid={`origano-harvest-${i + 1}`} className="group h-full rounded-2xl border border-[var(--border)] p-7 hover:border-[#83B94A]/60 transition-colors duration-500">
                      <div className="flex items-center justify-between">
                        <span className="font-jetbrains text-[11px] text-[var(--gold)]">{s.n}</span>
                        <span className="w-10 h-10 rounded-full bg-[#103F31]/10 flex items-center justify-center text-[#103F31] group-hover:bg-[#103F31] group-hover:text-[#F2F0E8] transition-colors duration-500">
                          <Icon className="w-4 h-4" />
                        </span>
                      </div>
                      <h3 className="font-cormorant text-2xl text-[var(--text-primary)] mt-5">{s.t}</h3>
                      <p className="font-outfit font-light text-sm text-[var(--text-secondary)] leading-relaxed mt-2">{s.d}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* TRACEABILITY */}
        <section data-testid="origano-trace" className="py-16 md:py-24 bg-[#103F31] text-[#F2F0E8]">
          <div className="max-w-[1100px] mx-auto px-6 md:px-10 text-center">
            <SectionLabel>{o.traceLabel}</SectionLabel>
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl leading-[1.05] tracking-tight mt-6">{o.traceTitle}</h2>
              <p className="font-outfit font-light text-[#F2F0E8]/80 leading-relaxed mt-6 max-w-3xl mx-auto">{o.traceText}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <Link to="/brevetti/etichetta-intelligente" data-testid="origano-trace-cta" className="inline-flex items-center gap-3 mt-10 rounded-full border border-[#83B94A] px-8 py-4 font-outfit text-xs tracking-[0.2em] uppercase text-[#83B94A] hover:bg-[#83B94A] hover:text-[#103F31] transition-colors duration-300">
                {o.traceCta} <ArrowUpRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </section>

        {/* SHOP COMING SOON */}
        <section data-testid="origano-shop" className="py-20 md:py-32">
          <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--gold)]/60 px-5 py-2 font-jetbrains text-[10px] tracking-[0.25em] uppercase text-[var(--gold)]">
              <ShoppingBag className="w-3.5 h-3.5" /> {o.shopLabel}
            </span>
            <Reveal>
              <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)] mt-8">{o.shopTitle}</h2>
              <p className="font-outfit font-light text-[var(--text-secondary)] leading-relaxed mt-6 max-w-2xl mx-auto">{o.shopText}</p>
            </Reveal>
          </div>
        </section>

        <LeadForm source="origano" interests={o.interests} label={o.shopLabel} />
      </main>
      <Footer />
    </div>
  );
}
